import { useState } from 'react'
import { IconButton, Badge, Box } from '@mui/material'
import { BellIcon } from '@heroicons/react/24/outline'
import useAppState from '../../../hooks/useAppState'
import { useVisibleInAppNotifications } from '../../hooks/useVisibleInAppNotifications'
import { NotificationSidebar } from './NotificationSidebar'
import { getActiveOrgRole, isParticipantRole } from '../../../features/organization/orgStateHelpers'

interface Props {
  userId: string | undefined
}

const GAMER_BELL_GRACE_PERIOD_MS = 5 * 60 * 1000

export const NotificationBell = ({ userId }: Props) => {
  const [open, setOpen] = useState(false)
  const { state } = useAppState()
  const visibleNotifications = useVisibleInAppNotifications()
  const unviewedCount = visibleNotifications.filter((n) => !n.viewedAt).length
  const isParticipant = isParticipantRole(state.user)
  const hasRecentlyCreatedNotification = visibleNotifications.some((notification) => {
    const createdAtMs = new Date(notification.createdAt).getTime()
    return Number.isFinite(createdAtMs) && Date.now() - createdAtMs <= GAMER_BELL_GRACE_PERIOD_MS
  })
  const shouldShowBell = !open && isParticipant && (unviewedCount > 0 || hasRecentlyCreatedNotification)

  if (!userId || !isParticipant) return null

  return (
    <>
      {shouldShowBell && (
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 10, sm: 12, md: 12 },
            right: { xs: 16, sm: 24, md: 24 },
            zIndex: 9998
          }}
        >
          <IconButton
            onClick={() => setOpen(true)}
            aria-label={`Notifications${unviewedCount > 0 ? `, ${unviewedCount} unread` : ''}`}
            size="small"
            sx={{
              color: 'common.white',
              backgroundColor: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(4px)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' }
            }}
          >
            <Badge badgeContent={unviewedCount} color="error" max={99}>
              <BellIcon width={22} height={22} />
            </Badge>
          </IconButton>
        </Box>
      )}
      <NotificationSidebar open={open} onClose={() => setOpen(false)} orgRole={getActiveOrgRole(state.user)} />
    </>
  )
}
